import React, { useState, useEffect, useMemo, useRef } from 'react'
import { createPortal } from 'react-dom'
import { listEmployees } from '@/api/workforce'
import { Employee } from '@/types/workforce'
import { useImpersonation } from '@/hooks/useImpersonation'
import { useAuth } from '@/hooks/useAuth'
import { User, ChevronDown, Search, Users, Loader2, Shield, Eye } from 'lucide-react'
import { getTenantDomain } from '@/lib/services/api-utils'
import { Z_INDEX } from '@/lib/constants/z-index'

interface AdminUserSwitcherProps {
  onUserChange?: () => void
}

export default function AdminUserSwitcher({ onUserChange }: AdminUserSwitcherProps) {
  const { user: authUser } = useAuth()
  const { canImpersonate, isImpersonating, startImpersonation, stopImpersonation } = useImpersonation()
  
  const [isOpen, setIsOpen] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedCategory, setSelectedCategory] = useState('all')
  const buttonRef = useRef<HTMLButtonElement>(null)
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 })

  // Async data state
  const [employees, setEmployees] = useState<Employee[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Load data on component mount
  useEffect(() => {
    const loadData = async () => {
      try {
        setIsLoading(true)
        setError(null)

        // Fetch real employees from workforce API
        const tenantDomain = getTenantDomain()
        const result = await listEmployees(tenantDomain, {
          status: 'ACTIVE',
          limit: 300
        })

        setEmployees(result.items)
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load employees')
        console.error('❌ Error loading AdminUserSwitcher employees:', err)
      } finally {
        setIsLoading(false)
      }
    }

    loadData()
  }, [])

  // Filter employees based on search and category
  const filteredEmployees = useMemo(() => {
    if (!employees.length) return []

    return employees.filter(emp => {
      const name = emp.personalInfo?.name || ''
      const department = emp.employment?.department?.name || ''
      const role = emp.employment?.role?.name || ''

      const matchesSearch = name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           department.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           role.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           emp.email.toLowerCase().includes(searchTerm.toLowerCase())

      return matchesSearch
    }).slice(0, 12) // Limit to 12 results
  }, [employees, searchTerm])

  const handleUserSelect = async (employeeId: string | number, email: string, name: string) => {

    if (!canImpersonate) {
      console.error('❌ User does not have permission to impersonate')
      return
    }

    // Use impersonation with employeeId (convert to string if needed)
    const employeeIdStr = String(employeeId)

    // Start or switch impersonation
    const success = await startImpersonation(employeeIdStr)

    if (success) {
      setIsOpen(false)
      setSearchTerm('')
      onUserChange?.()
    } else {
      console.error('❌ startImpersonation failed')
    }
  }

  const handleStopImpersonation = async () => {
    if (isImpersonating) {
      await stopImpersonation()
      setIsOpen(false)
    }
  }

  // Debug logging

  // Don't render if user can't impersonate
  if (!canImpersonate && !isImpersonating) {
    return null
  }

  // Calculate dropdown position when opening
  const handleToggle = () => {
    if (!isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect()
      setDropdownPosition({
        top: rect.bottom + 8,
        left: rect.left
      })
    }
    setIsOpen(!isOpen)
  }

  return (
    <div className="relative">
      {/* Trigger Button */}
      <button
        ref={buttonRef}
        onClick={handleToggle}
        className={`btn-ghost text-sm flex items-center gap-2 group ${
          isImpersonating ? 'text-warning hover:text-warning-light' : ''
        }`}
      >
        <div className="relative">
          <User className="w-4 h-4 group-hover:scale-110 transition-transform duration-200" />
          {canImpersonate && (
            <Shield className="w-2 h-2 absolute -top-1 -right-1 text-warning" />
          )}
        </div>
        <span className="font-display font-medium">
          {isImpersonating ? 'Personificando' : 'Trocar Usuário'}
        </span>
        <ChevronDown className={`w-4 h-4 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {/* Dropdown - Rendered as Portal */}
      {isOpen && createPortal(
        <div
          className="w-96 bg-brand-dark-light/95 backdrop-blur-sm border border-gray-700/50 rounded-monumenta shadow-monumenta-xl max-h-96 overflow-hidden animate-fade-in"
          style={{
            position: 'fixed',
            top: dropdownPosition.top,
            left: dropdownPosition.left,
            zIndex: Z_INDEX.DROPDOWN
          }}
        >
          {/* Header */}
          <div className="p-4 border-b border-gray-700/50">
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-display text-white font-bold flex items-center gap-2">
                {canImpersonate && <Shield className="w-4 h-4 text-warning" />}
                {canImpersonate ? 'Personificação de Funcionário' : 'Ver Funcionários'}
              </h3>

              {/* Stop impersonation button */}
              {isImpersonating && (
                <button
                  onClick={handleStopImpersonation}
                  className="btn-sm btn-outline-warning"
                >
                  Sair da Personificação
                </button>
              )}
            </div>

            {/* <p className="text-brand-gray-light text-xs font-body">
              {canImpersonate
                ? isImpersonating
                  ? 'Clique em outro funcionário para trocar ou saia da personificação'
                  : 'Clique em qualquer funcionário para personificá-lo e ver sua visão exata'
                : 'Ver informações dos funcionários'
              }
            </p> */}
          </div>

          {/* Search */}
          <div className="p-3 border-b border-gray-700/50">
            <div className="relative group">
              <Search className="w-4 h-4 absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 group-focus-within:text-brand-primary transition-colors duration-200 pointer-events-none z-10" />
              <input
                type="text"
                placeholder="Buscar por nome, departamento ou função..."
                className="form-input pl-10 pr-4 py-2 text-sm"
                style={{ paddingLeft: '3rem' }}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>

          {/* Search Results */}
          <div className="max-h-64 overflow-y-auto">
            {isLoading ? (
              <div className="p-8 text-center">
                <Loader2 className="w-8 h-8 mx-auto mb-4 text-brand-primary animate-spin" />
                <p className="text-brand-gray text-sm font-body">Carregando funcionários...</p>
              </div>
            ) : error ? (
              <div className="p-4 text-center text-red-400">
                <p className="text-sm font-body">Erro: {error}</p>
              </div>
            ) : filteredEmployees.length > 0 ? (
              <div className="p-2">
                <div className="space-y-1">
                  {filteredEmployees.map((emp) => {
                    const name = emp.personalInfo?.name || emp.email
                    const initials = name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase()

                    return (
                      <button
                        key={emp.employeeId}
                        onClick={() => handleUserSelect(emp.employeeId, emp.email, name)}
                        className="w-full text-left px-3 py-2 hover:bg-brand-dark-lighter rounded-monumenta transition-all duration-200 group border border-transparent hover:border-brand-primary/20"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 bg-gradient-to-br from-brand-blue to-brand-accent-600 rounded-full flex items-center justify-center text-white text-xs font-bold shadow-monumenta group-hover:shadow-monumenta-brand transition-all duration-200">
                            {initials}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-white text-sm font-medium truncate font-display group-hover:text-brand-primary transition-colors duration-200">{name}</div>
                            <div className="text-brand-gray text-xs truncate font-body">{emp.email}</div>
                          </div>
                          <div className="flex items-center gap-2">
                            {canImpersonate && (
                              <Eye className="w-3 h-3 text-warning opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
                            )}
                          </div>
                        </div>
                      </button>
                    )
                  })}
                </div>
              </div>
            ) : searchTerm !== '' ? (
              <div className="p-4 text-center text-brand-gray">
                <Users className="w-8 h-8 mx-auto mb-2" />
                <p className="text-sm font-body">Nenhum funcionário encontrado</p>
              </div>
            ) : null}
          </div>

          {/* Footer */}
          <div className="p-3 border-t border-gray-700/50 bg-brand-dark/50">
            <div className="flex items-center justify-between text-xs text-brand-gray font-body">
              <span>Total: {employees.length} funcionários</span>
              {isImpersonating && (
                <div className="flex items-center gap-1 text-warning">
                  <Eye className="w-3 h-3" />
                  <span>Personificando</span>
                </div>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  )
}
